import axios from "axios";
import { BASE_URL } from "utils/constants";

/**
 * Converts an axios error into the result object returned to callers.
 * - No `err.response` (real network failure): keeps the legacy
 *   `{ status: "desconnected", message: "No internet connection" }`.
 * - HTTP error (401, 500, ...): `{ status: "error", httpStatus, message }`
 *   with the message taken from the response body when available.
 * Callers that check `res.status === "success"` keep working unchanged.
 * @param {Error} err - Error thrown by axios.
 * @param {string} label - Method name, used for the 401 warning.
 * @returns {{status: string, message: string, httpStatus?: number}}
 */
export const toErrorResult = (err, label = "NotifyService") => {
  const response = err && err.response;
  if (!response) {
    return {
      status: "desconnected",
      message: "No internet connection",
    };
  }
  const httpStatus = response.status;
  const body = response.data;
  const message =
    (body && typeof body === "object" && body.message) ||
    (typeof body === "string" && body) ||
    err.message ||
    "Request failed";
  if (httpStatus === 401) {
    console.warn(`[NotifyService.${label}] 401 Unauthorized: ${message}`);
  }
  return { status: "error", httpStatus, message };
};

class NotifyService {
  async request(label, method, path, data) {
    const url = `${BASE_URL}/api/notify/${path}`;
    try {
      const response = await axios[method](url, data);
      return response.data;
    } catch (err) {
      return toErrorResult(err, label);
    }
  }

  async getNotify(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request("getNotify", "get", `get${query ? "?" + query : ""}`);
  }

  async markAllRead() {
    return this.request("markAllRead", "put", "markAllRead");
  }

  async getPreferences() {
    return this.request("getPreferences", "get", "preferences");
  }

  async updatePreferences(data) {
    return this.request("updatePreferences", "put", "preferences", data);
  }

  async getNotifyOld(data) {
    return this.request("getNotifyOld", "post", "old/get", data);
  }

  async updateNotify(data) {
    return this.request("updateNotify", "put", "update", data);
  }

  async updateNotificationList() {
    return this.request(
      "updateNotificationList",
      "put",
      "update/notificationList"
    );
  }

  async userNotifications(data) {
    return this.request(
      "userNotifications",
      "put",
      "update/userNotifications",
      data
    );
  }
}

export default new NotifyService();
